import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { ArrowRight, ArrowUpRight, EnvelopeSimple, InstagramLogo, LinkedinLogo } from "@phosphor-icons/react";
import { EASE_OUT, MaskWords, PageFrame, Seo } from "../../kit.jsx";
import { CopyEmailButton, MAIL, Rise, SOCIALS, mailto } from "./shared.jsx";
import "./join.css";

const ROUTES = [
  {
    key: "students",
    label: "Students",
    subject: "Programme question",
    include: ["Which programme or page you are asking about", "Your question in a few lines", "Your age group, if it affects eligibility"],
    read: [["For students", "/for-students"], ["Upcoming events", "/programmes/upcoming"]],
  },
  {
    key: "schools",
    label: "Schools",
    subject: "School enquiry",
    include: ["Who you represent, and a teacher's contact details", "Your location, student age range and approximate group size", "The activity you have in mind and your preferred dates"],
    read: [["For schools", "/for-schools"]],
  },
  {
    key: "partners",
    label: "Partners",
    subject: "Partnership proposal",
    include: ["Who you represent", "What you can contribute and when you are available", "Any conditions, and whether it is one event or a longer collaboration"],
    read: [["For partners", "/for-partners"], ["Existing relationships", "/partnerships"]],
  },
  {
    key: "team",
    label: "Team",
    subject: "Team enquiry",
    include: ["The area you want to contribute to", "A brief example of relevant work", "Your weekly availability"],
    read: [["Join the team", "/student-opportunities/team"]],
  },
  {
    key: "press",
    label: "Press",
    subject: "Press question",
    include: ["Your publication and what you are working on", "The programme, figure or photo you are asking about", "Your deadline, if you have one"],
    read: [["Our impact", "/impact"], ["Photo library", "/media"]],
  },
  {
    key: "corrections",
    label: "Corrections",
    subject: "Photo or content correction",
    include: ["The page address", "The text or image concerned", "The correction or removal you are requesting"],
    read: [["Privacy", "/privacy"]],
    warn: "Avoid sending identity documents or other sensitive information.",
  },
];

function EmailHero() {
  const reduce = useReducedMotion();
  const [user, domain] = MAIL.split("@");
  const chars = [...user, "@", ...domain];
  return (
    <a className="jn-ct-mail" href={mailto()} aria-label={`Email ${MAIL}`}>
      <span className="jn-ct-mail__text" aria-hidden="true">
        {chars.map((char, index) => (
          <span key={`${char}-${index}`} className="jn-ct-mail__mask">
            <motion.span
              initial={reduce ? false : { y: "105%" }}
              animate={{ y: "0%" }}
              transition={{ duration: 0.9, delay: 0.15 + index * 0.022, ease: EASE_OUT }}
            >
              {char}
            </motion.span>
          </span>
        )).reduce((out, node, index) => (index === user.length ? [...out, <wbr key="wbr" />, node] : [...out, node]), [])}
      </span>
      <span className="jn-ct-mail__rule" aria-hidden="true" />
    </a>
  );
}

function Router() {
  const reduce = useReducedMotion();
  const [picked, setPicked] = useState(0);
  const route = ROUTES[picked];
  return (
    <section className="jn-wrap jn-ct-route" aria-labelledby="jn-ct-route-title">
      <header className="jn-ct-route__head">
        <MaskWords id="jn-ct-route-title" className="jn-h2" text="Who is writing?" />
        <p className="jn-lede">Pick one. We fill in the subject line and list what to include, so your first note has what we need.</p>
      </header>
      <div className="jn-ct-route__tabs" role="tablist" aria-label="Choose who is writing">
        {ROUTES.map((item, index) => (
          <button
            key={item.key}
            id={`jn-ct-tab-${item.key}`}
            type="button"
            role="tab"
            aria-selected={picked === index}
            aria-controls="jn-ct-panel"
            className={`jn-pill ${picked === index ? "is-on" : ""}`}
            onClick={() => setPicked(index)}
          >
            {picked === index && (
              <motion.span className="jn-pill__fill" layoutId="jn-ct-fill" transition={{ duration: reduce ? 0 : 0.45, ease: EASE_OUT }} />
            )}
            <span className="jn-pill__label">{item.label}</span>
          </button>
        ))}
      </div>
      <div className="jn-ct-route__panel" id="jn-ct-panel" role="tabpanel" aria-labelledby={`jn-ct-tab-${route.key}`}>
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={route.key}
            className="jn-ct-route__grid"
            initial={reduce ? false : { opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            exit={reduce ? undefined : { opacity: 0, y: -10 }}
            transition={{ duration: 0.35, ease: EASE_OUT }}
          >
            <div className="jn-ct-route__subject">
              <span className="jn-mono">Subject line</span>
              <strong>{route.subject}</strong>
              <a className="gh-btn gh-btn--fill" href={mailto(route.subject)}>
                Open email with this subject <ArrowUpRight weight="bold" aria-hidden="true" />
              </a>
            </div>
            <div className="jn-ct-route__include">
              <span className="jn-mono">Include</span>
              <ul>
                {route.include.map((item) => <li key={item}><span className="jn-tick" aria-hidden="true" />{item}</li>)}
              </ul>
              {route.warn && <p className="jn-ct-route__warn">{route.warn}</p>}
            </div>
            <div className="jn-ct-route__read">
              <span className="jn-mono">Read first</span>
              {route.read.map(([label, to]) => (
                <Link key={to} className="gh-link" to={to}>{label} <ArrowRight weight="bold" aria-hidden="true" /></Link>
              ))}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
}

export default function ContactPage() {
  return (
    <PageFrame className="rd-page jn-page jn-ct">
      <Seo
        title="Contact"
        path="/contact"
        description="Contact Project GRID about programmes, student participation, mentorship and partnerships."
      />
      <main>
        <header className="jn-wrap jn-ct-hero">
          <Rise as="p" className="jn-mono" delay={0.05}>Contact</Rise>
          <MaskWords as="h1" className="jn-ct-hero__title" text="Start with a clear note." onMount delay={0.05} />
          <EmailHero />
          <Rise className="jn-ct-hero__row" delay={0.7}>
            <p className="jn-lede">
              One address for programme questions, school enquiries, collaborations and corrections. Tell us who you are, what you are trying to do and the most useful next conversation.
            </p>
            <div className="gh-actions">
              <a className="gh-btn gh-btn--fill" href={mailto()}>
                <EnvelopeSimple weight="bold" aria-hidden="true" />
                <span className="roll" data-label="Open your email app"><span>Open your email app</span></span>
              </a>
              <CopyEmailButton />
            </div>
          </Rise>
          <p className="jn-small jn-ct-hero__fine">The button opens an email composer. It does not send a message. If no mail app is set up, copy the address instead.</p>
        </header>

        <Router />

        <section className="jn-wrap jn-ct-channels" aria-labelledby="jn-ct-channels-title">
          <h2 id="jn-ct-channels-title" className="jn-mono">Other channels</h2>
          <ul>
            <li>
              <a href={mailto()}>
                <EnvelopeSimple weight="bold" aria-hidden="true" />
                <strong>Email</strong>
                <span>{MAIL}</span>
                <small>Questions, enquiries and corrections</small>
                <ArrowUpRight weight="bold" aria-hidden="true" />
              </a>
            </li>
            <li>
              <a href={SOCIALS.instagram} target="_blank" rel="noreferrer">
                <InstagramLogo weight="bold" aria-hidden="true" />
                <strong>Instagram</strong>
                <span>@projectgrid_org</span>
                <small>Announcements and registration updates</small>
                <ArrowUpRight weight="bold" aria-hidden="true" />
              </a>
            </li>
            <li>
              <a href={SOCIALS.linkedin} target="_blank" rel="noreferrer">
                <LinkedinLogo weight="bold" aria-hidden="true" />
                <strong>LinkedIn</strong>
                <span>project-grid-global</span>
                <small>Programme results</small>
                <ArrowUpRight weight="bold" aria-hidden="true" />
              </a>
            </li>
          </ul>
        </section>
      </main>
    </PageFrame>
  );
}
